import { EvidenceOpportunitySchema, type EvidenceOpportunity, type Continuity, type RouteStep } from '@dissidium/contracts';
import { add } from '../kernel/primitives.ts';
import { validateLedger } from '../continuity/ledger.ts';

export type RouteState = { facts: string[]; money: number; used: string[] };
export function executeRouteStep(state: RouteState, step: RouteStep, c: Continuity): RouteState {
  if (state.used.includes(step.id)) throw new Error('A route action cannot be farmed');
  if (step.requires.some(f => !state.facts.includes(f)) || step.forbids.some(f => state.facts.includes(f))) throw new Error('Route prerequisites or obligations prevent this action');
  if (step.cost > state.money) throw new Error('This route is unaffordable');
  if (step.witnessId) {
    const witness = c.persons[step.witnessId];
    if (!witness || witness.lifecycle !== 'active' || !witness.location || state.facts.includes(`hostile:${step.witnessId}`)) throw new Error('The living witness is unavailable');
  }
  if (step.consentFact && !state.facts.includes(step.consentFact)) throw new Error('This action needs consent');
  if (step.itemId && (!c.items[step.itemId] || ['consumed', 'bequest'].includes(c.items[step.itemId]!.allocation.kind))) throw new Error('The required object is unavailable');
  if (step.merchantFact && !state.facts.includes(step.merchantFact)) throw new Error('The required merchant is unavailable');
  return { facts: [...new Set([...state.facts, ...step.grants])].sort(), money: add(state.money, -step.cost), used: [...state.used, step.id] };
}
export function executeWitness(opportunity: EvidenceOpportunity, c: Continuity, trace: string[]): RouteState {
  const o = EvidenceOpportunitySchema.parse(opportunity);
  validateLedger(c);
  if (o.requiresLegacy && !c.legacyIds.some(id => c.persons[id]?.lifecycle === 'active')) throw new Error('A direct continuant opportunity needs a real available legacy');
  let state: RouteState = { facts: [...o.initialFacts].sort(), money: o.budget, used: [] };
  for (const id of trace) {
    const step = o.steps.find(s => s.id === id);
    if (!step) throw new Error('Unknown evidence route step');
    state = executeRouteStep(state, step, c);
  }
  if (!state.facts.includes(o.goal)) throw new Error('The witness trace did not establish the finding');
  return state;
}
export function solveEvidence(input: EvidenceOpportunity, c: Continuity): string[] {
  const o = EvidenceOpportunitySchema.parse(input);
  validateLedger(c);
  if (new Set(o.steps.map(s => s.id)).size !== o.steps.length) throw new Error('Duplicate route step');
  if (o.requiresLegacy && !c.legacyIds.some(id => c.persons[id]?.lifecycle === 'active')) throw new Error('No eligible continuant for this promised observation');
  // Only actions that can supply the finding or its prerequisites can help.
  // Facts only accumulate and costs are nonnegative in this D00 contract.
  const needed = new Set([o.goal]);
  const relevant = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const step of o.steps) if (!relevant.has(step.id) && step.grants.some(f => needed.has(f))) {
      relevant.add(step.id); changed = true;
      for (const fact of [...step.requires, step.consentFact, step.merchantFact]) if (fact) needed.add(fact);
    }
  }
  const steps = o.steps.filter(step => relevant.has(step.id)).sort((a, b) => a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  const queue: RouteState[] = [{ facts: [...o.initialFacts].sort(), money: o.budget, used: [] }];
  const forbiddenFacts = new Set(steps.flatMap(step => step.forbids));
  const dominates = (a: RouteState, b: RouteState): boolean => a.money >= b.money
    && b.facts.every(f => a.facts.includes(f))
    && [...forbiddenFacts].every(f => a.facts.includes(f) === b.facts.includes(f));
  let frontier = [...queue];
  let expanded = 0;
  // ponytail: 256 nondominated expansions; larger authored puzzles need a measured budget.
  for (let cursor = 0; cursor < queue.length; cursor++) {
    const state = queue[cursor]!;
    if (!frontier.includes(state)) continue;
    if (expanded++ === 256) throw new Error(`Evidence search budget exceeded for ${o.id}; simplify the capsule or raise its measured budget`);
    if (state.facts.includes(o.goal)) { executeWitness(o, c, state.used); return state.used; }
    for (const step of steps) {
      let next: RouteState;
      try { next = executeRouteStep(state, step, c); } catch { continue; }
      if (frontier.some(state => dominates(state, next))) continue;
      frontier = frontier.filter(state => !dominates(next, state));
      frontier.push(next); queue.push(next);
    }
  }
  throw new Error(`No feasible authorized evidence route for ${o.id}`);
}
